import { Button, Table, Spinner } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { checkPermissionByKey } from "../utils/checkPermissionByKey";
import { config } from "../config/config";
import { getProvidersWithJoinSessionPermission } from "../config/url";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { FaSearch } from "react-icons/fa";
import AddProvider from "./AddProvider";

const PROVIDER_TYPES = [
  "Therapist",
  "Care Manager",
  "Psychiatric Consultant",
];

const STATUS_OPTIONS = ["Active", "Pending", "Inactive"];

const STATUS_COLORS = {
  Active: { bg: "#D1FAE5", color: "#047857", border: "#A7F3D0" },
  Pending: { bg: "#FFEDD5", color: "#C2410C", border: "#FDBA74" },
  Inactive: { bg: "#FEE2E2", color: "#DC2626", border: "#FECACA" },
};

const US_STATE_ABBR = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  "District of Columbia": "DC",
};

const toDisplayStatus = (rawStatus) => {
  const status = String(rawStatus || "deactivated").toLowerCase();
  if (status === "active") return "Active";
  if (status === "deactivated" || status === "inactive") return "Inactive";
  return "Pending";
};

const abbreviateState = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  return US_STATE_ABBR[trimmed] || trimmed;
};

const getProviderStates = (provider) => {
  let credentials = provider?.credentials;
  if (typeof credentials === "string") {
    try {
      credentials = JSON.parse(credentials);
    } catch {
      credentials = [];
    }
  }
  if (!Array.isArray(credentials)) credentials = [];

  const fromCredentials = credentials
    .filter((c) => {
      if (!c || typeof c === "string") return Boolean(c);
      const status = String(c.status || "Active").toLowerCase();
      return status !== "inactive";
    })
    .map((c) => (typeof c === "string" ? c : c?.state))
    .filter(Boolean);
  const topLevel = provider?.state
    ? Array.isArray(provider.state)
      ? provider.state
      : [provider.state]
    : [];
  const combined = [...fromCredentials, ...topLevel];
  return [...new Set(combined.map(abbreviateState).filter(Boolean))];
};

const StatusPill = ({ status }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  return (
    <span
      className="providers-status-pill"
      style={{
        backgroundColor: colors.bg,
        color: colors.color,
        borderColor: colors.border,
      }}
    >
      {status}
    </span>
  );
};

const StateTags = ({ states }) => {
  if (!states.length) {
    return <span className="providers-state-empty">-</span>;
  }

  const visible = states.slice(0, 2);
  const overflow = states.length - visible.length;

  return (
    <div className="providers-state-tags">
      {visible.map((state) => (
        <span key={state} className="providers-state-tag">
          {state}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="providers-state-tag providers-state-overflow"
          title={states.slice(2).join(", ")}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
};

const ActiveProviders = () => {
  const user = useSelector((state) => state?.user);
  const navigate = useNavigate();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [providers, setProviders] = useState([]);
  const [providersPerPage, setProvidersPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [stateFilter, setStateFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [availableStates, setAvailableStates] = useState([]);
  const [showAddDrawer, setShowAddDrawer] = useState(false);

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;
  const canList = checkPermissionByKey(user, "providerManagement", "ProviderList");
  const canAdd = checkPermissionByKey(user, "providerManagement", "AddProvider");
  const canView = checkPermissionByKey(
    user,
    "providerManagement",
    "ViewProviderProfile",
  );

  const fetchProviders = useCallback(async () => {
    if (!canList) {
      setProviders([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${getProvidersWithJoinSessionPermission}?businessId=${businessId}&businessIdentifier=${businessIdentifier}`,
        config,
      );
      const list = Array.isArray(response.data?.data) ? response.data.data : [];
      setProviders(list);
    } catch (error) {
      console.error("Error fetching providers:", error);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, businessIdentifier, canList]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    if (location.state?.openAddDrawer) {
      setShowAddDrawer(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/states/all`,
        );
        if (response.data?.success) {
          const sorted = (response.data.data || [])
            .slice()
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setAvailableStates(sorted);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    fetchStates();
  }, []);

  const filteredProviders = useMemo(() => {
    const searchLower = search.trim().toLowerCase();

    return providers.filter((provider) => {
      const fullName = `${provider.firstname || ""} ${provider.lastname || ""}`
        .trim()
        .toLowerCase();
      const matchesSearch =
        !searchLower ||
        fullName.includes(searchLower) ||
        provider.email?.toLowerCase().includes(searchLower) ||
        provider.providerId?.toLowerCase().includes(searchLower);

      const providerType = provider.providerType || "Therapist";
      const matchesType = typeFilter === "All" || providerType === typeFilter;

      const displayStatus = toDisplayStatus(provider.status);
      const matchesStatus =
        statusFilter === "All" || displayStatus === statusFilter;

      const providerStates = getProviderStates(provider);
      const matchesState =
        stateFilter === "All" ||
        providerStates.includes(abbreviateState(stateFilter)) ||
        providerStates.includes(stateFilter);

      return matchesSearch && matchesType && matchesStatus && matchesState;
    });
  }, [providers, search, typeFilter, stateFilter, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProviders.length / providersPerPage) || 1,
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const currentProviders = useMemo(() => {
    const start = (currentPage - 1) * providersPerPage;
    return filteredProviders.slice(start, start + providersPerPage);
  }, [currentPage, filteredProviders, providersPerPage]);

  const hasActiveFilters =
    search.trim() !== "" ||
    typeFilter !== "All" ||
    stateFilter !== "All" ||
    statusFilter !== "All";

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("All");
    setStateFilter("All");
    setStatusFilter("All");
    setCurrentPage(1);
  };

  const handleViewProvider = (provider) => {
    navigate(`/providers/${provider._id}`, {
      state: { provider },
    });
  };

  return (
    <div className="app-page providers-page">
      <div className="providers-page-header">
        <h2 className="app-page-title">Providers</h2>

        <div className="providers-page-filters">
          <div className="app-filter-search-wrap providers-search-wrap">
            <FaSearch className="app-filter-search-icon" />
            <input
              type="text"
              className="app-filter-search"
              placeholder="Search providers..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <select
            className="app-filter-select"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Types</option>
            {PROVIDER_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          <select
            className="app-filter-select"
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All States</option>
            {availableStates.map((state) => (
              <option key={state._id || state.name} value={state.name}>
                {abbreviateState(state.name) || state.name}
              </option>
            ))}
          </select>

          <select
            className="app-filter-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Statuses</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="providers-clear-btn"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Clear
          </button>

          {canAdd && (
            <Button
              className="app-filter-action-btn providers-add-btn"
              onClick={() => setShowAddDrawer(true)}
            >
              + Add Provider
            </Button>
          )}
        </div>
      </div>

      <div className="providers-table-shell">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">Loading providers...</div>
          </div>
        ) : filteredProviders.length === 0 ? (
          <div className="text-center p-4">No providers found.</div>
        ) : (
          <>
            <div className="providers-table-wrap">
              <Table className="admin-table providers-table mb-0" hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Licenses</th>
                    <th>Status</th>
                    <th className="providers-actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProviders.map((provider) => {
                    const displayStatus = toDisplayStatus(provider.status);
                    const states = getProviderStates(provider);
                    const name =
                      `${provider.firstname || ""} ${provider.lastname || ""}`.trim() ||
                      "N/A";
                    const role = provider.providerType || "Therapist";

                    return (
                      <tr key={provider._id}>
                        <td className="providers-name-cell">{name}</td>
                        <td className="providers-role-cell">{role}</td>
                        <td>
                          <StateTags states={states} />
                        </td>
                        <td>
                          <StatusPill status={displayStatus} />
                        </td>
                        <td className="providers-actions-col">
                          {canView ? (
                            <button
                              type="button"
                              className="providers-view-btn"
                              onClick={() => handleViewProvider(provider)}
                            >
                              View
                            </button>
                          ) : (
                            <span style={{ color: "#9CA3AF" }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <TablePaginationBar
              currentPage={currentPage}
              totalItems={filteredProviders.length}
              rowsPerPage={providersPerPage}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={(n) => {
                setProvidersPerPage(n);
                setCurrentPage(1);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              hideRangeLabel
              className="providers-pagination"
            />
          </>
        )}
      </div>

      <AddProvider
        open={showAddDrawer}
        onClose={() => setShowAddDrawer(false)}
        onSuccess={() => {
          setShowAddDrawer(false);
          fetchProviders();
        }}
      />
    </div>
  );
};

export default ActiveProviders;
